import axios from "@/utils/axios";
import {
	AgentBuyQuotaResponse,
	BuyAgentQuotaDto,
	CreateMyAgentReportRequest,
	CreateMyAgentReportResponse,
	CreateMyAgentRequest,
	CreateMyAgentResponse,
	CreateMyOperatorRequest,
	CreateMyOperatorResponse,
	GetAgentBuyQuotaActivityLogRequest,
	GetAgentBuyQuotaActivityLogResponse,
	GetAgentResponse,
	GetAgentsMeResponse,
	GetAgentsResponse,
	GetMyAgentReportRequest,
	GetMyAgentReportResponse,
	GetMyOperatorActivityLogRequest,
	GetMyOperatorActivityLogResponse,
	GetMyOperatorOrderSummaryRequest,
	GetMyOperatorOrderSummaryResponse,
	GetMyOperatorsRequest,
	GetMyOperatorsResponse,
	GetMySubAgentOrderSummaryRequest,
	GetMySubAgentOrderSummaryResponse,
	GetMySubAgentReportRequest,
	GetMySubAgentReportResponse,
	GetMySubAgentsRequest,
	GetMySubAgentsResponse,
	GetMyWinningQuotaRequest,
	GetMyWinningQuotaResponse,
	ResetMyOperatorQuotaRequest,
	ResetMyOperatorQuotaResponse,
	UpdateMyAgentReportRequest,
	UpdateMyAgentReportResponse,
	UpdateMyOperatorRequest,
	UpdateMyOperatorResponse,
	OperatorModel,
	WithdrawMyOperatorQuotaRequest,
	UpdateMyRequest,
	UpdateMyResponse,
	UpdateMySubAgentRequest,
	UpdateMySubAgentResponse,
	GetAgentQuotaLogRequest,
	GetAgentQuotaLogResponse,
	GetMyProviderRatesRequest,
	GetMyProviderRatesResponse,
} from "@/types/agent";
import { TwoFactorStatus } from "@/types/auth";
import {
	FxRate,
	GetFxRateRequest,
	OperatorQuotaResetPreview,
} from "@/types/currency";

export const getAgentsMe = async (): Promise<GetAgentsMeResponse> => {
	try {
		const response = await axios.get(`/agents/me`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgents = async (): Promise<GetAgentsResponse> => {
	try {
		const response = await axios.get(`/agents`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgent = async (id: string): Promise<GetAgentResponse> => {
	try {
		const response = await axios.get(`/agents${id}`);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMyOperators = async (
	payload: GetMyOperatorsRequest,
): Promise<GetMyOperatorsResponse> => {
	try {
		const response = await axios.post(`/agents/get-my-operators`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMySubAgents = async (
	payload: GetMySubAgentsRequest,
): Promise<GetMySubAgentsResponse> => {
	try {
		const response = await axios.post(`/agents/get-my-sub-agents`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMyOperatorOrderSummary = async (
	payload: GetMyOperatorOrderSummaryRequest,
): Promise<GetMyOperatorOrderSummaryResponse> => {
	try {
		const response = await axios.post(
			`/agents/get-my-operator-order-summary`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMySubAgentOrderSummary = async (
	payload: GetMySubAgentOrderSummaryRequest,
): Promise<GetMySubAgentOrderSummaryResponse> => {
	try {
		const response = await axios.post(
			`/agents/get-my-sub-agent-order-summary`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createMyOperator = async (
	payload: CreateMyOperatorRequest,
): Promise<CreateMyOperatorResponse> => {
	try {
		const response = await axios.post(
			`/agents/create-my-operator-request`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createMyAgent = async (
	payload: CreateMyAgentRequest,
): Promise<CreateMyAgentResponse> => {
	try {
		const response = await axios.post(
			`/agents/create-my-agent-request`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMyWinningQuota = async (
	payload: GetMyWinningQuotaRequest,
): Promise<GetMyWinningQuotaResponse> => {
	try {
		const response = await axios.post(`/agents/get-my-winning-quota`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateMy = async (
	payload: UpdateMyRequest,
): Promise<UpdateMyResponse> => {
	try {
		const response = await axios.put(`/agents/update-my`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateMyOperator = async (
	payload: UpdateMyOperatorRequest,
): Promise<UpdateMyOperatorResponse> => {
	try {
		const response = await axios.put(`/agents/update-my-operator`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

// Take back part of an operator's unused quota into the agent's own (ดึงเครดิตออก).
export const withdrawMyOperatorQuota = async (
	payload: WithdrawMyOperatorQuotaRequest,
): Promise<OperatorModel> => {
	try {
		const response = await axios.post(
			`/agents/withdraw-my-operator-quota`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMyProviderRates = async (
	payload: GetMyProviderRatesRequest,
): Promise<GetMyProviderRatesResponse> => {
	try {
		const response = await axios.post(`/agents/get-my-provider-rates`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateMySubAgent = async (
	payload: UpdateMySubAgentRequest,
): Promise<UpdateMySubAgentResponse> => {
	try {
		const response = await axios.put(`/agents/update-my-sub-agent`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMyAgentReport = async (
	payload: GetMyAgentReportRequest,
): Promise<GetMyAgentReportResponse> => {
	try {
		const response = await axios.post(`/agents/get-my-agent-report`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const createMyAgentReport = async (
	payload: CreateMyAgentReportRequest,
): Promise<CreateMyAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/agents/create-my-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const updateMyAgentReport = async (
	payload: UpdateMyAgentReportRequest,
): Promise<UpdateMyAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/agents/update-my-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMySubAgentReport = async (
	payload: GetMySubAgentReportRequest,
): Promise<GetMySubAgentReportResponse> => {
	try {
		const response = await axios.post(
			`/agents/get-my-sub-agent-report`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const resetMyOperatorQuota = async (
	payload: ResetMyOperatorQuotaRequest,
): Promise<ResetMyOperatorQuotaResponse> => {
	try {
		const response = await axios.post(
			`/agents/reset-my-operator-quota`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

// What resetting the Operator's quota would move, at the market rate, without moving it.
export const previewResetMyOperatorQuota = async (
	payload: ResetMyOperatorQuotaRequest,
): Promise<OperatorQuotaResetPreview> => {
	try {
		const response = await axios.post(
			`/agents/reset-my-operator-quota-preview`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

// The market rate quota moves at between the Agent's THB and an Operator's currency.
export const getFxRate = async (payload: GetFxRateRequest): Promise<FxRate> => {
	try {
		const response = await axios.post(`/agents/get-fx-rate`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getMyOperatorActivityLog = async (
	payload: GetMyOperatorActivityLogRequest,
): Promise<GetMyOperatorActivityLogResponse> => {
	try {
		const response = await axios.post(
			`/agents/get-my-operator-activity-log`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const purchaseQuota = async (
	payload: BuyAgentQuotaDto,
): Promise<AgentBuyQuotaResponse> => {
	try {
		const response = await axios.post(`/agents/buy-my-quota`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const purchaseQuotaCrypto = async (payload: {
	chain: string;
	winningQuotaAmount: number;
	note?: string;
}): Promise<{
	id: string;
	address: string;
	amount: number;
	currency: string;
}> => {
	try {
		const response = await axios.post(`/agents/buy-my-quota/crypto`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const confirmCryptoPayment = async (payload: {
	paymentId: string;
	transactionHash: string;
}): Promise<{ success: boolean; message: string }> => {
	try {
		const response = await axios.post(`/agents/buy-my-quota/crypto-confirm`, {
			id: payload.paymentId,
			txHash: payload.transactionHash,
		});
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentBuyQuotaActivityLog = async (
	payload: GetAgentBuyQuotaActivityLogRequest,
): Promise<GetAgentBuyQuotaActivityLogResponse> => {
	try {
		const response = await axios.post(
			`/agents/get-my-buy-quota-activity-log`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentBuyQuotaActivityLogSummary = async (
	payload: GetAgentBuyQuotaActivityLogRequest,
): Promise<{
	totalWinningQuotaBuy: number;
	totalWinningQuotaBuyUnsuccessful: number;
}> => {
	try {
		const response = await axios.post(
			`/agents/get-my-buy-quota-activity-log-summary`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const getAgentQuotaLog = async (
	payload: GetAgentQuotaLogRequest,
): Promise<GetAgentQuotaLogResponse> => {
	try {
		const response = await axios.post(`/agents/get-my-quota-log`, payload);
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

/**
 * Clear a 2FA factor the agent's own operator can no longer produce codes for.
 * The server proves the account really belongs to this agent before it writes.
 */
export const resetMyOperatorTwoFactor = async (
	operatorId: string,
	reason: string,
): Promise<TwoFactorStatus> => {
	try {
		const response = await axios.post(`/agents/reset-my-operator-2fa`, {
			operatorId,
			reason,
		});
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};

export const resetMySubAgentTwoFactor = async (
	subAgentId: string,
	reason: string,
): Promise<TwoFactorStatus> => {
	try {
		const response = await axios.post(`/agents/reset-my-sub-agent-2fa`, {
			subAgentId,
			reason,
		});
		return response.data;
	} catch (error: any) {
		throw error.response.data;
	}
};
